import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

const TECH_STACK = [
  { icon: 'ri-reactjs-line', label: 'React / Next.js' },
  { icon: 'ri-nodejs-line', label: 'Node.js' },
  { icon: 'ri-database-2-line', label: 'PostgreSQL / MongoDB' },
  { icon: 'ri-cloud-line', label: 'AWS / Azure / GCP' },
  { icon: 'ri-code-s-slash-line', label: 'TypeScript' },
  { icon: 'ri-shield-check-line', label: 'OAuth / JWT Security' },
];

const FEATURES = [
  { icon: 'ri-global-line', title: 'Enterprise Web Portals', desc: 'Scalable customer portals, partner dashboards, and SaaS platforms built with React/Next.js and enterprise-grade backends.' },
  { icon: 'ri-bar-chart-2-line', title: 'Real-Time Dashboards', desc: 'Live analytics dashboards with WebSocket data feeds, interactive charts, and AI-powered insights tailored to your KPIs.' },
  { icon: 'ri-brain-line', title: 'AI-Integrated Web Apps', desc: 'Web applications with embedded ML models, chatbots, recommendation engines, and predictive analytics built right in.' },
  { icon: 'ri-shopping-bag-line', title: 'eCommerce Platforms', desc: 'Custom-built eCommerce solutions with AI-driven product recommendations, personalized pricing, and smart inventory sync.' },
  { icon: 'ri-cloud-upload-line', title: 'Cloud-Native Architecture', desc: 'Microservices-based applications designed for horizontal scaling, high availability, and zero-downtime deployments.' },
  { icon: 'ri-smartphone-line', title: 'Progressive Web Apps (PWA)', desc: 'Offline-capable PWAs that deliver native-app experiences in the browser — installable, fast, and cross-platform.' },
];

const PROCESS = [
  { num: '01', icon: 'ri-search-eye-line', title: 'Requirements Discovery', desc: 'We map your workflows, users, and integration points to define a precise technical specification.' },
  { num: '02', icon: 'ri-artboard-line', title: 'UI/UX Design', desc: 'Wire-frames and high-fidelity prototypes reviewed and approved before a single line of code is written.' },
  { num: '03', icon: 'ri-code-box-line', title: 'Agile Development', desc: '2-week sprints with demos, so you see working software frequently and can adjust course early.' },
  { num: '04', icon: 'ri-rocket-2-line', title: 'Launch & Scale', desc: 'CI/CD pipelines, load testing, and post-launch support to keep performance exceptional as you grow.' },
];

export default function WebApplicationsPage() {
  const featRef = useRef<HTMLElement>(null);
  const featInView = useInView(featRef);
  const processRef = useRef<HTMLElement>(null);
  const processInView = useInView(processRef);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 bg-white overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-teal-50/70 blur-3xl -translate-y-1/4 translate-x-1/4" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="flex items-center gap-2 mb-5">
                <Link to="/services" className="text-gray-400 text-sm hover:text-teal-600 transition-colors cursor-pointer">Services</Link>
                <i className="ri-arrow-right-s-line text-gray-300" />
                <span className="text-teal-600 text-sm font-medium">Web Applications</span>
              </div>
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-5">Web Applications</span> */}
              <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-5">
                Modern Web Apps That <span className="text-teal-500">Scale With You</span>
              </h1>
              <p className="text-gray-500 text-xl leading-relaxed mb-8">
                We craft responsive, AI-enhanced web applications tailored to your business workflows — from customer portals and SaaS platforms to enterprise dashboards, built to grow with your organization.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {['React / Next.js', 'Cloud-Native', 'AI-Integrated', 'ISO 9001:2015 Quality'].map((b) => (
                  <span key={b} className="bg-teal-50 text-teal-700 border border-teal-200 text-xs font-medium px-4 py-1.5 rounded-full">{b}</span>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="cursor-pointer whitespace-nowrap bg-teal-500 hover:bg-teal-600 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors inline-flex items-center gap-2">
                  Start Your Project <i className="ri-arrow-right-line" />
                </Link>
                <Link to="/portfolio" className="cursor-pointer whitespace-nowrap bg-white hover:bg-gray-50 text-gray-700 font-semibold px-7 py-3.5 rounded-lg border border-gray-200 transition-colors">
                  View Portfolio
                </Link>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-gray-100">
              <div className="w-full h-[420px]">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20web%20application%20dashboard%20UI%20on%20laptop%20computer%20screen%20showing%20analytics%20charts%20clean%20minimal%20interface%20data%20visualization%20teal%20green%20accent%20responsive%20design%20white%20background%20professional%20software%20interface%20enterprise%20portal&width=700&height=500&seq=web-app-hero-001&orientation=landscape"
                  alt="Web Applications"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-teal-600 py-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[{ val: '200+', label: 'Web Apps Delivered' }, { val: '15+', label: 'Years Web Dev' }, { val: '99.9%', label: 'Uptime SLA' }, { val: '4.9★', label: 'Client Rating' }].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-3xl font-extrabold text-white">{s.val}</div>
                <div className="text-teal-200 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Tech Stack */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-extrabold text-gray-900">Technologies We Master</h2>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {TECH_STACK.map((t) => (
                <div key={t.label} className="flex items-center gap-2.5 bg-white border border-gray-200 rounded-xl px-6 py-3">
                  <div className="w-7 h-7 flex items-center justify-center text-teal-600">
                    <i className={`${t.icon} text-xl`} />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section ref={featRef} className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className={`text-center mb-14 transition-all duration-700 ${featInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4">What We Build</span> */}
              <h2 className="text-4xl font-extrabold text-gray-900">Web Solutions for Every Need</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f, i) => (
                <div
                  key={f.title}
                  className={`bg-gray-50 border border-gray-100 rounded-2xl p-7 hover:border-teal-200 hover:bg-teal-50/20 transition-all duration-500 ${featInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl mb-5">
                    <i className={`${f.icon} text-2xl`} />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-3">{f.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section ref={processRef} className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className={`text-center mb-12 transition-all duration-700 ${processInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4">Our Process</span> */}
              <h2 className="text-3xl font-extrabold text-gray-900">From Brief to Live in Record Time</h2>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              {PROCESS.map((step, i) => (
                <div
                  key={step.num}
                  className={`relative bg-white border border-gray-100 rounded-2xl p-7 hover:border-teal-200 transition-all duration-500 ${processInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="text-6xl font-extrabold text-teal-50 absolute top-4 right-6 leading-none select-none">{step.num}</div>
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl mb-4">
                    <i className={`${step.icon} text-2xl`} />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">{step.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-teal-600">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4">Ready to Build Your Web Application?</h2>
            <p className="text-teal-100 text-base mb-8">ISO-certified quality, 25 years of experience, and a team fully committed to your product's success.</p>
            <Link to="/contact" className="cursor-pointer whitespace-nowrap bg-white hover:bg-gray-50 text-teal-700 font-semibold px-8 py-4 rounded-xl transition-colors inline-flex items-center gap-2">
              <i className="ri-calendar-check-line" /> Get Free Consultation
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
